import { TFunction } from 'i18next';
import _get from 'lodash/get';
import {
  AttachmentCuInputObject,
  SsoIntegrationCreateInputObject,
  SsoIntegrationProviderPresetEnum,
  SsoIntegrationProviderTypeEnum,
  SsoIntegrationUpdateInputObject,
  SsoMappingField,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import mergeAndUnlinkAttachedFiles from '@lib/utils/mergeAndUnlinkAttachedFiles';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';
import { SsoIntegrationFieldNames } from './constants';
import getSsoPresetLabel from './getSsoPresetLabel';

type MappingEntry = {
  attribute: string;
  mapsTo: { value: string; label: string } | string | null;
  isDefault?: boolean;
};

function buildAttributeMappingJson(
  rows: MappingEntry[],
  supportedMappingFields: SsoMappingField[] = [],
): Record<string, string> {
  const fieldOrder = new Map(
    supportedMappingFields.map((f) => [f.key, f.sortIndex]),
  );
  const sorted = [...rows].sort((a, b) => {
    const getMapsToValue = (row: MappingEntry) =>
      row.mapsTo && typeof row.mapsTo === 'object'
        ? row.mapsTo.value
        : row.mapsTo;
    const av = getMapsToValue(a);
    const bv = getMapsToValue(b);
    const ai = av ? fieldOrder.get(av) ?? Infinity : Infinity;
    const bi = bv ? fieldOrder.get(bv) ?? Infinity : Infinity;
    return ai - bi;
  });
  const res = sorted.reduce<Record<string, string>>((acc, row) => {
    const mapsToValue =
      row.mapsTo && typeof row.mapsTo === 'object'
        ? row.mapsTo.value
        : row.mapsTo;
    if (row.attribute && mapsToValue) {
      acc[row.attribute] = mapsToValue;
    }
    return acc;
  }, {});
  return res;
}

export default function getPreparedDataFromValues(
  values: Values,
  isEdit?: boolean,
  t?: TFunction,
  supportedMappingFields: SsoMappingField[] = [],
  prevValues: Values | undefined = undefined,
): SsoIntegrationCreateInputObject | SsoIntegrationUpdateInputObject {
  const presetOption = _get(
    values,
    SsoIntegrationFieldNames.ProviderPreset,
  ) as SelectOption | null;
  const preset = presetOption?.value as SsoIntegrationProviderPresetEnum;

  const companyDomainOptions = _get(
    values,
    SsoIntegrationFieldNames.CompanyDomainIds,
    [],
  ) as SelectOption[];

  const currentDomainItems = companyDomainOptions.map((opt) => ({
    id: opt.value as string,
  }));
  const prevDomainOptions = _get(
    prevValues ?? {},
    SsoIntegrationFieldNames.CompanyDomainIds,
    [],
  ) as SelectOption[];
  const prevDomainItems = prevDomainOptions.map((opt) => ({
    id: opt.value as string,
  }));
  const companyDomainIds =
    mergeAndUnlinkMultiSelectValues(prevDomainItems, currentDomainItems) ??
    undefined;

  const attributeMappingRows = _get(
    values,
    SsoIntegrationFieldNames.AttributeMapping,
    [],
  ) as MappingEntry[];

  const attributeMapping = buildAttributeMappingJson(
    attributeMappingRows,
    supportedMappingFields,
  );

  // Wrap single icon value in an array so mergeAndUnlinkAttachedFiles can process it.
  // The utility strips `url` from new uploads, filters out unchanged existing icons
  // (those with `urls`), and maps `null` to `{ _destroy: true }`.
  const iconValue = _get(values, SsoIntegrationFieldNames.IconAttached);
  const iconAttachedInput: AttachmentCuInputObject | undefined = iconValue
    ? mergeAndUnlinkAttachedFiles(
        { [SsoIntegrationFieldNames.IconAttached]: [iconValue] },
        {},
        SsoIntegrationFieldNames.IconAttached,
      )[0]
    : undefined;

  const nameValue =
    (_get(values, SsoIntegrationFieldNames.Name, '') as string) || undefined;
  const presetLabel = t && preset ? getSsoPresetLabel(preset, t) : undefined;
  const shouldOmitName = !isEdit && nameValue === presetLabel;

  const baseData = {
    name: shouldOmitName ? undefined : nameValue,
    idpMetadata: _get(
      values,
      SsoIntegrationFieldNames.IdpMetadata,
      '',
    ) as string,
    companyDomainIds,
    attributeMapping,
    jitProvisioningEnabled:
      (_get(
        values,
        SsoIntegrationFieldNames.JitProvisioningEnabled,
      ) as boolean) ?? false,
    timeToleranceSeconds:
      Number(_get(values, SsoIntegrationFieldNames.TimeToleranceSeconds)) ||
      undefined,
    syncUserAttributes:
      (_get(values, SsoIntegrationFieldNames.SyncUserAttributes) as boolean) ??
      false,
    allowIdpInitiated:
      (_get(values, SsoIntegrationFieldNames.AllowIdpInitiated) as boolean) ??
      false,
    iconAttached: iconAttachedInput,
  };

  if (isEdit) {
    return baseData as SsoIntegrationUpdateInputObject;
  }

  return {
    ...baseData,
    providerPreset: preset,
    providerType: SsoIntegrationProviderTypeEnum.Saml,
    code:
      (_get(values, SsoIntegrationFieldNames.Code, '') as string) || undefined,
  } as SsoIntegrationCreateInputObject;
}
